import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { KeyRound, Loader2, Check, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function ResetPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [step, setStep] = useState<'form' | 'success' | 'error'>('form');
    const [error, setError] = useState('');
    const [resetEmail, setResetEmail] = useState('');

    // Validate passwords match
    const passwordsMatch = newPassword === confirmPassword;
    const passwordValid = newPassword.length >= 8;

    const resetMutation = useMutation({
        mutationFn: () => api.confirmPasswordReset(token!, newPassword),
        onSuccess: (data) => {
            setResetEmail(data.email);
            setStep('success');
        },
        onError: (err: Error) => {
            setError(err.message);
            setStep('error');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!passwordValid) {
            setError(t('settings.atLeast8Chars', 'Password must be at least 8 characters'));
            return;
        }
        if (!passwordsMatch) {
            setError(t('auth.resetPassword.passwordsMustMatch', 'Passwords must match'));
            return;
        }
        setError('');
        resetMutation.mutate();
    };

    // If no token, show error
    if (!token && step === 'form') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 px-4">
                <div className="w-full max-w-md">
                    <div className="card p-8 text-center">
                        <div className="w-16 h-16 bg-danger-100 dark:bg-danger-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle className="w-8 h-8 text-danger-600" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2 text-danger-600">
                            {t('auth.resetPassword.failed', 'Reset Failed')}
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                            {t('auth.resetPassword.invalidLink', 'This reset link is invalid or has expired.')}
                        </p>
                        <Link to="/forgot-password" className="btn-primary inline-flex">
                            {t('auth.resetPassword.tryAgain', 'Try Again')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 px-4">
            <div className="w-full max-w-md">
                <div className="card p-8">
                    {/* Reset Form */}
                    {step === 'form' && (
                        <>
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <KeyRound className="w-8 h-8 text-primary-600" />
                                </div>
                                <h1 className="text-2xl font-bold mb-2">
                                    {t('auth.resetPassword.title', 'Reset Password')}
                                </h1>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    {t('auth.resetPassword.newPasswordSubtitle', 'Enter your new password below')}
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="label">
                                        {t('auth.resetPassword.newPassword', 'New Password')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="input pr-10"
                                            placeholder="••••••••"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-neutral-500 mt-1">
                                        {t('settings.atLeast8Chars', 'At least 8 characters')}
                                    </p>
                                </div>

                                <div>
                                    <label className="label">
                                        {t('auth.resetPassword.confirmPassword', 'Confirm Password')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className={`input pr-10 ${confirmPassword && !passwordsMatch ? 'border-danger-500' : ''}`}
                                            placeholder="••••••••"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {confirmPassword && !passwordsMatch && (
                                        <p className="text-xs text-danger-500 mt-1">
                                            {t('auth.resetPassword.passwordsMustMatch', 'Passwords must match')}
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={resetMutation.isPending || !passwordValid || !passwordsMatch}
                                    className="btn-primary w-full"
                                >
                                    {resetMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {t('auth.resetPassword.resetting', 'Resetting...')}
                                        </>
                                    ) : (
                                        t('auth.resetPassword.resetButton', 'Reset Password')
                                    )}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link to="/login" className="text-sm text-neutral-500 hover:text-neutral-700">
                                    {t('twoFactor.backToLogin', '← Back to login')}
                                </Link>
                            </div>
                        </>
                    )}

                    {/* Success */}
                    {step === 'success' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-success-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">
                                {t('auth.resetPassword.success', 'Password Reset Complete!')}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {t('auth.resetPassword.successMessage', 'Your password has been reset. You can now login with your new password.')}
                            </p>
                            {resetEmail && (
                                <p className="text-sm text-neutral-500 mb-6">
                                    Email: <strong>{resetEmail}</strong>
                                </p>
                            )}
                            <button
                                onClick={() => navigate('/login')}
                                className="btn-primary"
                            >
                                {t('auth.login', 'Sign In')}
                            </button>
                        </div>
                    )}

                    {/* Error */}
                    {step === 'error' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-danger-100 dark:bg-danger-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-danger-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2 text-danger-600">
                                {t('auth.resetPassword.failed', 'Reset Failed')}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {error || t('auth.resetPassword.invalidLink', 'This reset link is invalid or has expired.')}
                            </p>
                            <Link to="/forgot-password" className="btn-primary inline-flex">
                                {t('auth.resetPassword.tryAgain', 'Try Again')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
